/**
 * SETTINGS — doc 18 §2, built in sections as their features land.
 *
 * The rule this screen keeps: **no row that does nothing.** A toggle for a
 * feature that has not been built yet is a lie told in the player's own
 * settings menu, so every row here was added with the thing it controls.
 *
 * Export copies the save to the clipboard and import reads a paste, so moving a
 * colony between machines never needs a file dialog or a line of DOM.
 */

import { L, S } from '../../content/strings.ts';
import { audio } from '../../audio/engine.ts';
import { AUTO_LURE_CHOICES, newProfile } from '../../meta/profile.ts';
import { ASSIST } from '../../meta/tree.ts';
import type { MotionChoice, Profile, UiScaleChoice } from '../../meta/types.ts';
import type { FrameOpts, MenuItem } from '../../ui/menu.ts';
import type { App } from '../app.ts';
import { newTutorial } from '../tutor.ts';
import { MenuScreen } from './menuscreen.ts';
import { TitleScreen } from './title.ts';

const UI_SCALES: UiScaleChoice[] = ['auto', 'small', 'normal', 'large'];
const MOTION: MotionChoice[] = ['auto', 'on', 'off'];
const FLASHES: Array<'full' | 'reduced' | 'off'> = ['full', 'reduced', 'off'];

/** Row ids, so the handler names what it changes instead of counting rows. */
type Row =
  | 'volMaster'
  | 'volMusic'
  | 'volSfx'
  | 'volAmbience'
  | 'uiScale'
  | 'fullscreen'
  | 'edgeScroll'
  | 'minimap'
  | 'screenShake'
  | 'flash'
  | 'damageNumbers'
  | 'reducedMotion'
  | 'assist'
  | 'autoLure'
  | 'confirmSell'
  | 'export'
  | 'import'
  | 'runLog'
  | 'replay'
  | 'reset'
  | 'back';

function next<T>(list: T[], current: T): T {
  const at = list.indexOf(current);
  return list[(at + 1) % list.length]!;
}

export class SettingsScreen extends MenuScreen {
  private note: string | null = null;
  /** RESET THIS COLONY is armed by the first press and fired by the second. */
  private resetArmed = false;
  private rows: Row[] = [];

  constructor(app: App) {
    super(app);
    window.addEventListener('paste', this.onPaste);
  }

  private readonly onPaste = (e: ClipboardEvent): void => {
    const text = e.clipboardData?.getData('text') ?? '';
    if (text.length === 0 || !this.app.slot) return;
    const err = this.app.store.importSlot(this.app.slot, text);
    if (err) {
      this.note = err;
    } else {
      const loaded = this.app.store.loadSlot(this.app.slot).profile;
      if (loaded) this.app.useProfile(this.app.slot, loaded);
      this.note = S.settings.saveImportedWelcome;
    }
    this.dirty = true;
  };

  protected frame(): FrameOpts {
    return {
      title: S.settings.settings,
      subtitle: this.note ?? S.settings.oceanDoesNot,
      footer: S.settings.enterChangesRow,
      stamp: this.app.profile?.name,
    };
  }

  protected items(): MenuItem[] {
    const p = this.app.profile;
    const s = p?.settings;
    const rows: Array<MenuItem & { id: Row }> = [
      this.volumeRow('volMaster', S.settings.master, S.settings.audio, S.settings.everythingIncludingParts),
      this.volumeRow('volMusic', S.settings.music, undefined, S.settings.droneGetsFloor),
      this.volumeRow('volSfx', S.settings.sfx, undefined, S.settings.harpoonsMortarsLure),
      this.volumeRow('volAmbience', S.settings.ambience, undefined, S.settings.waterItselfSteps),
      {
        id: 'uiScale',
        section: S.settings.display,
        label: S.settings.uiScale,
        value: (s?.uiScale ?? 'auto').toUpperCase(),
        hint: `EVERYTHING IN THE INTERFACE, ONE STEP BIGGER OR SMALLER. NOW: ${this.app.uiScale}X`,
      },
      {
        id: 'fullscreen',
        label: S.settings.fullscreen,
        value: document.fullscreenElement ? 'ON' : S.settings.off,
        hint: S.settings.oceanLargeWindow,
      },
      {
        id: 'edgeScroll',
        label: S.settings.edgeScroll,
        value: s?.edgeScroll === false ? S.settings.off : 'ON',
        hint: S.settings.panShovingPointer,
      },
      {
        id: 'minimap',
        label: S.settings.minimap,
        value: s?.minimap === false ? S.settings.off : 'ON',
        hint: S.settings.bottomRightPanel,
      },
      {
        id: 'screenShake',
        section: S.settings.feel,
        label: S.settings.screenShake,
        value: s?.screenShake === false ? S.settings.off : 'ON',
        hint: S.settings.mortarsPxBosses,
      },
      {
        id: 'flash',
        label: S.settings.flashIntensity,
        value: (s?.flash ?? 'full').toUpperCase(),
        hint: S.settings.clampsBrightEffects,
      },
      {
        id: 'damageNumbers',
        label: S.settings.damageNumbers,
        value: s?.damageNumbers === false ? S.settings.off : 'ON',
        hint: S.settings.oneNumberPer,
      },
      {
        id: 'reducedMotion',
        label: S.settings.reducedMotion,
        value: (s?.reducedMotion ?? 'auto').toUpperCase(),
        hint: this.app.reducedMotion
          ? S.settings.bannersHoldInstead
          : S.settings.autoFollowsSystem,
      },
      {
        id: 'assist',
        section: S.settings.game,
        label: S.settings.calmCurrents,
        value: s?.assist ? 'ON' : S.settings.off,
        tone: s?.assist ? 'good' : 'ink',
        hint: this.assistHint(p),
      },
      {
        id: 'autoLure',
        label: S.settings.autoLure,
        value: s?.autoLureSeconds ? `${s.autoLureSeconds} S` : S.settings.off,
        hint: s?.autoLureSeconds
          ? L.autoLureOn(s.autoLureSeconds)
          : S.settings.offNothingArrives,
      },
      {
        id: 'confirmSell',
        label: S.settings.confirmBeforeSell,
        value: s?.confirmSell ? 'ON' : S.settings.off,
        hint: S.settings.xOnceAsk,
      },
      {
        id: 'export',
        section: S.settings.data,
        label: S.settings.exportSaveClipboard,
        hint: S.settings.copyFishAnother,
        disabled: !this.app.slot,
      },
      {
        id: 'import',
        label: S.settings.importPressCtrl,
        hint: S.settings.pasteSaveOver,
        disabled: true,
      },
      {
        id: 'runLog',
        label: S.settings.exportRunLog,
        value: p ? L.runsLogged(p.runLog.length) : '',
        hint: S.settings.lastRunsAs,
        disabled: !p || p.runLog.length === 0,
      },
      {
        id: 'replay',
        label: S.settings.replayFirstDescent,
        hint: this.taught()
          ? S.settings.reArmOnboarding
          : S.settings.nothingReplayYet,
        disabled: !this.taught(),
      },
      {
        id: 'reset',
        label: this.resetArmed ? S.settings.resetPressEnter : S.settings.resetThisColony,
        tone: this.resetArmed ? 'critical' : 'warning',
        hint: this.resetArmed
          ? L.resetStakes(p?.pearls ?? 0, p?.owned.length ?? 0)
          : S.settings.startThisSlot,
        disabled: !p,
      },
      { id: 'back', label: S.settings.back, hotkey: 'B' },
    ];
    this.rows = rows.map((r) => r.id);
    return rows;
  }

  /** Doc 18 §1: a 0..100 slider that steps in tens, because this is a menu. */
  private volumeRow(
    id: 'volMaster' | 'volMusic' | 'volSfx' | 'volAmbience',
    label: string,
    section: string | undefined,
    hint: string,
  ): MenuItem & { id: Row } {
    const value = this.app.profile?.settings[id] ?? 0;
    const filled = Math.round(value / 10);
    const row: MenuItem & { id: Row } = {
      id,
      label,
      value: `${'|'.repeat(filled)}${'.'.repeat(10 - filled)} ${String(value).padStart(3)}`,
      hint: audio.running ? hint : `${hint} (SOUND STARTS ON YOUR FIRST CLICK.)`,
    };
    if (section !== undefined) row.section = section;
    return row;
  }

  private taught(): boolean {
    const t = this.app.profile?.tutorial;
    return t !== undefined && (t.done || t.seen.length > 0 || t.released.length > 0);
  }

  private assistHint(p: Profile | null): string {
    const bonus = Math.round((ASSIST.coreHpBonus + ASSIST.perLossBonus * (p?.assistLosses ?? 0)) * 100);
    return p?.settings.assist
      ? L.assistOn(Math.min(Math.round(ASSIST.maxBonus * 100), bonus), Math.round(ASSIST.perLossBonus * 100), Math.round(ASSIST.maxBonus * 100))
      : L.assistOff(Math.round(ASSIST.coreHpBonus * 100));
  }

  protected activate(index: number): void {
    const row = this.rows[index];
    const p = this.app.profile;
    if (row !== 'reset') this.resetArmed = false;
    if (row && p) this.change(row, p);
    if (row === 'export') this.exportSave();
    if (row === 'runLog') this.exportRunLog();
    if (row === 'fullscreen') void this.toggleFullscreen();
    if (row === 'back') this.back();
    this.dirty = true;
  }

  /** Everything that edits the profile, named rather than counted by index. */
  private change(row: Row, p: Profile): void {
    const s = p.settings;
    switch (row) {
      case 'volMaster':
      case 'volMusic':
      case 'volSfx':
      case 'volAmbience':
        s[row] = s[row] >= 100 ? 0 : s[row] + 10;
        this.app.applyVolumes();
        audio.play(row === 'volMusic' ? 'nudge' : 'ui_select'); // hear the change
        break;
      case 'uiScale':
        s.uiScale = next(UI_SCALES, s.uiScale);
        this.app.applyUiScale();
        break;
      case 'edgeScroll':
        s.edgeScroll = !s.edgeScroll;
        break;
      case 'minimap':
        s.minimap = !s.minimap;
        break;
      case 'screenShake':
        s.screenShake = !s.screenShake;
        break;
      case 'flash':
        s.flash = next(FLASHES, s.flash);
        break;
      case 'damageNumbers':
        s.damageNumbers = !s.damageNumbers;
        break;
      case 'reducedMotion':
        s.reducedMotion = next(MOTION, s.reducedMotion);
        break;
      case 'assist':
        s.assist = !s.assist;
        this.note = s.assist
          ? S.settings.oceanHasAgreed
          : S.settings.calmCurrentsOff;
        break;
      case 'autoLure':
        s.autoLureSeconds = next([...AUTO_LURE_CHOICES], s.autoLureSeconds);
        break;
      case 'confirmSell':
        s.confirmSell = !s.confirmSell;
        break;
      case 'replay':
        // doc 19: per-profile, so this re-arms THIS colony and no other
        p.tutorial = newTutorial();
        this.note = S.settings.firstDescentArmed;
        break;
      case 'reset':
        if (!this.resetArmed) {
          this.resetArmed = true;
          return; // nothing written yet: the second press is the decision
        }
        this.resetArmed = false;
        this.resetColony(p);
        break;
      default:
        return;
    }
    this.app.persist();
  }

  private resetColony(p: Profile): void {
    const slot = this.app.slot;
    if (!slot) return;
    const fresh = newProfile(p.name, Date.now());
    fresh.settings = { ...p.settings }; // the menu is not part of the colony
    this.app.useProfile(slot, fresh);
    this.app.persist();
    this.note = S.settings.colonyGoneSettings;
  }

  private async toggleFullscreen(): Promise<void> {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen();
    } catch {
      this.note = S.settings.browserSaidNo;
    }
    this.dirty = true;
  }

  private exportSave(): void {
    const slot = this.app.slot;
    if (!slot) return;
    const text = this.app.store.exportSlot(slot);
    if (!text) {
      this.note = S.settings.nothingExport;
      return;
    }
    this.copy(text, S.settings.saveCopiedClipboard);
  }

  /** Doc 18 §7: the run log leaves this machine only if the player sends it. */
  private exportRunLog(): void {
    const p = this.app.profile;
    if (!p || p.runLog.length === 0) return;
    this.copy(
      JSON.stringify({ colony: p.name, version: p.schemaVersion, runs: p.runLog }, null, 1),
      L.runLogCopied(p.runLog.length),
    );
  }

  private copy(text: string, ok: string): void {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        this.note = ok;
        this.dirty = true;
      })
      .catch(() => {
        this.note = S.settings.browserRefusedClipboard;
        this.dirty = true;
      });
  }

  protected override back(): void {
    this.app.go((a) => new TitleScreen(a));
  }

  override destroy(): void {
    window.removeEventListener('paste', this.onPaste);
  }
}
